import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { EncryptionService } from 'src/app/shared/services/encryption.service';
import { MaintenanceService } from 'src/app/feature/maintenance/services/maintenance.service';
import { SignInResponse } from 'src/app/shared/models/sign-in.model';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private apiUrl = environment.baseUrl + 'auth/';

  private loginTokenSubject = new BehaviorSubject<string | null>(null);
  loginToken$ = this.loginTokenSubject.asObservable();

  private loginDataSubject = new BehaviorSubject<any>({});
  loginData$ = this.loginDataSubject.asObservable();

  private maskedEmailSubject = new BehaviorSubject<string | null>(null);
  maskedEmail$ = this.maskedEmailSubject.asObservable();

  constructor(
    private http: HttpClient,
    private encryptionService: EncryptionService,
    private maintenanceService: MaintenanceService
  ) {}

  setLoginToken(token: string | null) {
    this.loginTokenSubject.next(token);
  }

  setLoginData(data: any) {
    this.loginDataSubject.next(data);
  }

  getLoginToken(): Observable<string | null> {
    return this.loginToken$;
  }

  getLoginData() {
    return this.loginData$;
  }

  setMaskedEmail(email: string | null) {
    this.maskedEmailSubject.next(email);
  }

  getMaskedEmail(): string | null {
    return this.maskedEmailSubject.getValue();
  }

  sendLoginOtp() {
    return this.http.post(this.apiUrl + 'send-otp', null);
  }

  validateLoginOtp(code: string, statusTerms: boolean, changePin = false) {
    const params = {
      channel: 'EMAIL',
      otpNumber: code,
      acceptTerms: statusTerms,
      changePin,
    };
    return this.http.put(this.apiUrl + 'valid-otp', params);
  }

  validateLoginPin(
    valuePin: string,
  ): Promise<{ valid: boolean; encryptedPin: string }> {
    return this.encryptPin(valuePin).then((encryptedPin) =>
      firstValueFrom(
        this.http.post<{ valid?: boolean }>(this.apiUrl + 'validate-pin', {
          pin: encryptedPin,
        }),
      ).then((response) => ({
        valid: response?.valid === true,
        encryptedPin,
      })),
    );
  }

  signInWithEncryptedPin(encryptedPin: string): Promise<SignInResponse> {
    return firstValueFrom(
      this.http.post<SignInResponse>(this.apiUrl + 'sign-in', {
        valuePin: encryptedPin,
      }),
    );
  }

  recoveryPin() {
    return this.http.post(this.apiUrl + 'recovery-pin', {});
  }

  signIn(valuePin: string): Promise<SignInResponse> {
    return this.encryptPin(valuePin).then(encryptedPin => {
      const params = {
        valuePin: encryptedPin
      };
      return firstValueFrom(
        this.http.post<SignInResponse>(this.apiUrl + 'sign-in', params),
      );
    });
  }

  /**
   * Cifra el PIN usando la llave pública
   * @param pin PIN en texto plano
   * @returns Promise<string> PIN cifrado
   */
  private async encryptPin(pin: string): Promise<string> {
    try {
      const publicKey = this.maintenanceService.getPublicKey();
      
      if (!publicKey) {
        await firstValueFrom(this.maintenanceService.checkMaintenanceStatus());
        const updatedPublicKey = this.maintenanceService.getPublicKey();
        
        if (!updatedPublicKey) {
          throw new Error('No se pudo obtener la llave pública del servidor');
        }
        
        return await this.encryptionService.encryptWithRSA(pin, updatedPublicKey);
      }
      
      return await this.encryptionService.encryptWithRSA(pin, publicKey);
    } catch (error) {
      throw new Error('Error al procesar la información de seguridad');
    }
  }

  refreshTokenSession() {
    return this.http.post(this.apiUrl + 'refresh-token', {});
  }

  logout() {
    return this.http.post(this.apiUrl + 'sign-out', {});
  }
}
